import React, { useState, useEffect, useMemo, useContext } from "react";
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths, startOfDay, isBefore, addDays } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { TokenContext } from "../components/TokenContext";
import { callProxy } from "../api/api";
import { fetchPackageBookings } from "../api/packageAvailability";

const DAY_SIZE = 38;

export default function SearchAvailabilityCalendar({ type, apartment, apartmentId, pkg, onChange }) {
  const { token } = useContext(TokenContext);
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [priceMap, setPriceMap] = useState({});
  const [blockedDates, setBlockedDates] = useState(new Set());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const today = startOfDay(new Date());
  


  // === Fetch apartment rates and iCal ===
  useEffect(() => {
    if (type !== "apartment" || !apartmentId) return;

    const loadApartmentRates = async () => {
      try {
        const res = await callProxy(`/api/rates/${apartmentId}`, token);
        const apartmentRates = res?.data?.data[String(apartmentId)] || {};

        const map = {};
        const blocked = new Set();

        Object.entries(apartmentRates).forEach(([date, info]) => {
          if (date === "ical_url") return;
          const price = info?.price ?? info?.dailyPrice ?? info?.price?.total ?? 0;
          map[date] = { price: Number(price), available: info?.available !== false };
          if (info?.available === false) blocked.add(date);
        });

        setPriceMap(map);
        setBlockedDates(blocked);

        // Fetch iCal events
        const icalUrl = apartment?.ical_url;
        if (icalUrl) {
          const icalRes = await fetch(icalUrl);
          const icsText = await icalRes.text();
          const events = icsText.split("BEGIN:VEVENT").slice(1);
          const newBlocked = new Set(blocked);

          events.forEach(event => {
            const startLine = event.match(/DTSTART(?:;VALUE=DATE)?:([\dT]+)/);
            const endLine = event.match(/DTEND(?:;VALUE=DATE)?:([\dT]+)/);
            if (startLine && endLine) {
              const parseDate = str => `${str.slice(0,4)}-${str.slice(4,6)}-${str.slice(6,8)}`;
              const start = new Date(parseDate(startLine[1]));
              const end = new Date(parseDate(endLine[1]));
              for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
                newBlocked.add(format(d, "yyyy-MM-dd"));
              }
            }
          });

          setBlockedDates(newBlocked);
        }
      } catch (err) {
        console.error("Apartment rates/iCal fetch failed:", err);
      }
    };

    loadApartmentRates();
  }, [type, apartmentId, apartment, token]);

  // === Fetch package bookings ===
  useEffect(() => {
    if (type !== "package" || !pkg?.slug) return;

    const loadPackageBookings = async () => {
      try {
        const res = await fetchPackageBookings(pkg.slug);
        const bookings = Array.isArray(res?.bookings) ? res.bookings : [];
        const blocked = new Set();
        bookings.forEach(b => {
          const start = new Date(b.checkIn);
          const end = new Date(b.checkOut);
          let curr = new Date(start);
          while (curr < end) {
            blocked.add(format(curr, "yyyy-MM-dd"));
            curr.setDate(curr.getDate() + 1);
          }
        });
        setBlockedDates(blocked);

        // map prices
        const map = {};
        const nights = pkg?.durationNights || 1;
        bookings.forEach(b => {
          const start = new Date(b.checkIn);
          const dateStr = format(start, "yyyy-MM-dd");
          map[dateStr] = { price: pkg.price, available: !blocked.has(dateStr) };
        });
        setPriceMap(map);
      } catch (err) {
        console.error("Package bookings fetch failed:", err);
      }
    };

    loadPackageBookings();
  }, [type, pkg]);

  // === Handle day click ===
// const handleDayClick = (dateStr) => {
//   if (type === "apartment") {
//     // First click → select check-in
//     if (!checkIn || (checkIn && checkOut)) {
//       setCheckIn(dateStr);
//       setCheckOut("");
//       if (typeof onChange === "function") {
//         onChange({
//           checkIn: parseISO(dateStr),
//           checkOut: null,
//           totalPrice: 0,
//           closeCalendar: false, // don't close yet
//         });
//       }
//       return;
//     }

//     // Second click → select check-out
//     const checkInDate = parseISO(checkIn);
//     const checkOutDate = parseISO(dateStr);

//     if (checkOutDate < checkInDate) {
//       // If checkout is before check-in, reset check-in
//       setCheckIn(dateStr);
//       setCheckOut("");
//       if (typeof onChange === "function") {
//         onChange({
//           checkIn: parseISO(dateStr),
//           checkOut: null,
//           totalPrice: 0,
//           closeCalendar: false,
//         });
//       }
//       return;
//     }

//     // ✅ Set checkout and notify parent to close
//     setCheckOut(dateStr);
//     if (typeof onChange === "function") {
//       onChange({
//         checkIn: checkInDate,
//         checkOut: checkOutDate,
//         totalPrice: priceMap[dateStr]?.price || 0,
//         closeCalendar: true, // triggers parent to close calendar
//       });
//     }
//   } else if (type === "package") {
//     const duration = pkg?.durationNights || 1;
//     const start = new Date(dateStr);
//     const end = addDays(start, duration);

//     setCheckIn(format(start, "yyyy-MM-dd"));
//     setCheckOut(format(end, "yyyy-MM-dd"));

//     if (typeof onChange === "function") {
//       onChange({
//         checkIn: start,
//         checkOut: end,
//         totalPrice: priceMap[dateStr]?.price || pkg.price,
//         closeCalendar: true, // close calendar
//       });
//     }
//   }
// };

// Inside SearchAvailabilityCalendar
const handleDayClick = (dateStr) => {
  if (type === "apartment" || !type) {
    if (!checkIn || (checkIn && checkOut)) {
      // Start new selection
      setCheckIn(dateStr);
      setCheckOut("");
      onChange?.({ checkIn: parseISO(dateStr), checkOut: null, totalPrice: 0, closeCalendar: false });
    } else {
      const checkInDate = parseISO(checkIn);
      const clickedDate = parseISO(dateStr);

      if (isBefore(clickedDate, checkInDate)) {
        // Click before check-in resets
        setCheckIn(dateStr);
        setCheckOut("");
        onChange?.({ checkIn: clickedDate, checkOut: null, totalPrice: 0, closeCalendar: false });
      } else if (dateStr === checkIn) {
        // Reset if same date
        setCheckIn("");
        setCheckOut("");
      } else {
        // Successful checkout selection
        setCheckOut(dateStr);
        const nights = Math.ceil((clickedDate - checkInDate) / (1000*60*60*24));
        const totalPrice = nights * (priceMap[dateStr]?.price || 0);
        onChange?.({ checkIn: checkInDate, checkOut: clickedDate, totalPrice, closeCalendar: true });
      }
    }
  } else if (type === "package") {
    const start = parseISO(dateStr);
    const end = addDays(start, pkg?.durationNights || 1);
    onChange?.({ checkIn: start, checkOut: end, totalPrice: pkg.price, closeCalendar: true });
  }
};



  // Days Grid
    const daysOfMonth = useMemo(
    () =>
        eachDayOfInterval({
        start: startOfMonth(currentMonth),
        end: endOfMonth(currentMonth),
        }),
    [currentMonth]
    );


  const lowestPrice = useMemo(() => {
    const prices = daysOfMonth
      .map(date => {
        const dateStr = format(date, "yyyy-MM-dd");
        return priceMap[dateStr]?.price;
      })
      .filter(p => typeof p === "number" && p > 0);
    return prices.length ? Math.min(...prices) : null;
  }, [daysOfMonth, priceMap]);

  return (
    <div className="p-2 select-none">
      {/* Month Navigation */}
        <div className="flex justify-between items-center mb-4 px-2">
        <button
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            className="p-1 rounded  bg-[#3a2a00]"
        >
            <ChevronLeft size={18} />
        </button>

        <div className="flex flex-col items-center">
            <span className="font-bold text-sm">{format(currentMonth, "MMMM yyyy")}</span>
            {lowestPrice && (
            <span className="text-[11px] text-emerald-600 font-semibold">
                From {lowestPrice} AED
            </span>
            )}
        </div>

        <button
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            className="p-1 rounded bg-[#3a2a00]"
        >
            <ChevronRight size={18} />
        </button>
        </div>


      {/* Days Grid */}
      <div className="grid grid-cols-7 gap-1">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map(d => (
          <div key={d} className="text-[10px] text-center font-bold text-slate-400">{d}</div>
        ))}
        {daysOfMonth.map(date => {
          const dateStr = format(date, "yyyy-MM-dd");
          const dayData = priceMap[dateStr];
          const isPast = isBefore(date, today);
          const isBooked = blockedDates.has(dateStr);
          const isDisabled = isPast || isBooked;
          const price = dayData?.price || null;

          const isSelected = dateStr === checkIn || dateStr === checkOut;
          const inRange = checkIn && checkOut && dateStr > checkIn && dateStr < checkOut;

          return (
            <div
            key={dateStr}
            onClick={() => !isDisabled && handleDayClick(dateStr)}
            className={`h-14 flex flex-col items-center justify-center rounded-lg cursor-pointer transition-all text-xs
                ${isDisabled ? "bg-gray-50 text-gray-400 cursor-not-allowed" : "hover:bg-gray-100"}
                ${isSelected ? "bg-yellow-100 border border-yellow-400 text-black font-semibold" : ""}
                ${inRange ? "bg-yellow-50 text-gray-800" : ""}`}
            >
            <span className={`${isBooked ? "line-through" : ""}`}>{date.getDate()}</span>
             {isBooked ? (
                    <span className="text-[7.5px] text-red-500 font-semibold"></span>
                ) : price ? (
                   <span className={`text-[8px] ${lowestPrice === price ? "text-green-600 font-bold" : "text-gray-500"}`}  style={{ fontSize: "8.2px" }}>
                {price} AED
            </span>

                ) : (
                    <span className="text-[2px] invisible">0</span>
                )}
            </div>

          );
        })}
      </div>
    </div>
  );
}
